// ===========================================
// useStudentWaitlist Hook
// ===========================================
// React Query hooks for student waitlist management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { studentWaitlistApi, AddWaitlistEntryInput } from '../api/studentWaitlist.api';

// ===========================================
// QUERY KEYS
// ===========================================

const studentWaitlistKeys = {
  all: ['studentWaitlist'],
  student: (studentId: string) => [...studentWaitlistKeys.all, 'student', studentId],
};

// ===========================================
// QUERY HOOKS
// ===========================================

/**
 * Get all waitlist entries for a student
 */
export function useStudentWaitlist(studentId: string) {
  return useQuery({
    queryKey: studentWaitlistKeys.student(studentId),
    queryFn: () =>
      studentWaitlistApi.getStudentWaitlist(studentId).then((res) => res.data),
    enabled: !!studentId,
  });
}

// ===========================================
// MUTATION HOOKS
// ===========================================

/**
 * Add a waitlist entry for a student
 */
export function useAddWaitlistEntry(studentId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: AddWaitlistEntryInput) =>
      studentWaitlistApi.addWaitlistEntry(studentId, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: studentWaitlistKeys.student(studentId),
      });
      enqueueSnackbar('Waitlist entry added', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to add waitlist entry';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Remove a waitlist entry
 */
export function useRemoveWaitlistEntry(studentId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (entryId: string) =>
      studentWaitlistApi.removeWaitlistEntry(studentId, entryId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: studentWaitlistKeys.student(studentId),
      });
      enqueueSnackbar('Waitlist entry removed', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to remove waitlist entry';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
